import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, MapPin, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import PageMeta from '../components/PageMeta';

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const result = await register(name, email, password);
      if (result.requires_verification) {
        setRegistrationSuccess(true);
        setRegisteredEmail(email);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ email: registeredEmail })
      });
      toast.success('Verification email resent!');
    } catch { toast.error('Failed to resend'); }
  };

  const features = [
    'AI-powered post generation',
    'Smart review replies',
    'Profile optimization audit',
    'Multi-language support'
  ];

  return (
    <div className="min-h-screen bg-white flex">
      <PageMeta title="Register - GMB Autopilot" />
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A73E8] items-center justify-center p-12">
        <div className="max-w-md">
          <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-8">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Start optimizing your local business today
          </h2>
          <p className="mt-4 text-white/80">
            Join thousands of businesses using GMB Autopilot to attract more customers.
          </p>
          <ul className="mt-8 space-y-3">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              GMB Autopilot
            </span>
          </Link>

          {registrationSuccess ? (
            <div className="text-center" data-testid="verification-message">
              <div className="w-16 h-16 bg-[#E6F4EA] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#1E8E3E]" />
              </div>
              <h1 className="text-2xl font-semibold text-[#202124] mb-2" style={{ fontFamily: 'Outfit' }}>Check Your Email</h1>
              <p className="text-[#5F6368] mb-6">We sent a verification link to <strong>{registeredEmail}</strong>. Please verify your email to activate your account.</p>
              <button onClick={handleResendVerification} className="text-[#1A73E8] hover:underline text-sm font-medium" data-testid="resend-verification-btn">
                Didn't receive it? Resend verification email
              </button>
              <div className="mt-6">
                <Link to="/login" className="btn-google inline-block">Go to Sign In</Link>
              </div>
            </div>
          ) : (
          <>
          <h1 className="text-3xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Create your account
          </h1>
          <p className="mt-2 text-[#5F6368]">
            Start with 3 free posts per day
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-google"
                placeholder="John Doe"
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-google"
                placeholder="you@example.com"
                required
                data-testid="register-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-google pr-12"
                  placeholder="Min. 6 characters"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368] hover:text-[#202124]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-google w-full flex items-center justify-center gap-2"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>

          <p className="mt-8 text-center text-[#5F6368]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1A73E8] font-medium hover:underline">
              Sign in
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
